import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  useTheme,
  TextField,
  InputAdornment,
} from "@mui/material";

import { QuestionAnswer } from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useNavigate } from "react-router-dom";

const InterviewsTable = ({ positions, loading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return positions ?? [];
    return (positions ?? []).filter((row) => {
      const title = (row?.title ?? "").toLowerCase();
      const jobId = String(row?.jobOpeningID ?? "").toLowerCase();
      return title.includes(q) || jobId.includes(q);
    });
  }, [positions, searchQuery]);

  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const renderTableBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            <Box
              sx={{
                py: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress
                size={40}
                thickness={4}
                sx={{ color: "primary.main" }}
              />
              <Typography
                sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
              >
                Loading job positions…
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (!positions || positions?.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            <Box
              sx={{
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontSize: "1rem",
                }}
              >
                No job positions found!
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return paginatedRows?.map((row) => (
      <TableRow
        key={row.id}
        hover
        sx={{
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "#f9fafb",
          },
        }}
      >
        <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
          {row.jobOpeningID || "-"}
        </TableCell>
        <TableCell sx={{ color: theme.palette.text.secondary }}>
          {row.title || "-"}
        </TableCell>
        <TableCell sx={{ color: theme.palette.text.secondary }}>
          {row?.questionCount || "-"}
        </TableCell>
        <TableCell>
          <Button
            variant="outlined"
            size="small"
            startIcon={<QuestionAnswer />}
            sx={{
              textTransform: "none",
              color: theme.palette.primary.main,
              borderColor: theme.palette.divider,
              fontWeight: 500,
              "&:hover": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-disabled": {
                color: theme.palette.text.disabled,
                borderColor: theme.palette.divider,
              },
            }}
            disabled={row?.questionCount <= 0}
            onClick={() => navigate(`/interviews/${row?.id}`)}
          >
            View
          </Button>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            Job Positions
          </Typography>

          <TextField
            size="small"
            placeholder="Search by job title or ID"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f9fafb",
                }}
              >
                {["Job ID", "Job Title", "Question", "Action"]?.map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        </TableContainer>

        {filteredRows.length > 0 && (
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              color: theme.palette.text.primary,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default InterviewsTable;
